import { Video } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';
import { media } from '@atlaskit/primitives/responsive';
import { css } from '@compiled/react';
import React from 'react';

import { OrVideo } from 'src/graphql/generated/graphql';
import { getAssetUrl } from 'src/services/http/user';
import { convertToSeconds } from 'src/pages/LessonPage/children/utils/mediaUtils';

import SectionHeading from './SectionHeading';

interface VideoSectionProps {
  video: OrVideo;
}

const VideoSection: React.FC<VideoSectionProps> = ({ video }) => {
  const chapters = video.chaptersCollection?.items.map(
    ({ chapterTitle, timeStamp }: { chapterTitle: string; timeStamp: string }) => ({
      time: convertToSeconds(timeStamp),
      label: chapterTitle,
    }),
  );

  const hasDescription = video.description?.length > 0;

  return (
    <div css={videoSectionContainerStyles}>
      {hasDescription && <SectionHeading heading={video.description} />}
      <div
        style={
          { '--rovo-hub-video-background': `url(${getAssetUrl('/images/hubs/rovoHubVideoBackground.svg')})` } as Record<
            string,
            string
          >
        }
        css={videoSectionStyles}
      >
        <Video
          name={video.name}
          chapters={chapters}
          videoId={video.sys.id}
          youtubeUrl={video.youtubeUrl}
          orangeUrl={video.cdnUrl}
          vimeoUrl={video.vimeoUrl}
          width={video.width}
          height={video.height}
          captions={video.captionCdnUrl}
          poster={video.banner}
          controls={true}
        />
      </div>
    </div>
  );
};

const videoSectionContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: token('space.300'),
  alignSelf: 'stretch',
  textAlign: 'center',
});

const videoSectionStyles = css({
  backgroundImage: 'var(--rovo-hub-video-background)',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  maxWidth: '810px',
  width: '100%',
  aspectRatio: '810 / 500', // Maintain background aspect ratio
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: token('space.400'), // Inset padding to create the inset effect
  paddingRight: token('space.300'), // Inset padding to create the inset effect

  '& > div': {
    // Target the video container
    width: '100%',
    aspectRatio: '16 / 9', // Standard video aspect ratio
  },

  '& iframe, & video': {
    width: '100%',
    height: '100%',
    border: 'none',
  },

  // Responsive adjustments
  [media.below.md]: {
    padding: '40px 30px',
    paddingLeft: token('space.300'), // Inset padding to create the inset effect
    paddingRight: token('space.200'), // Inset padding to create the inset effect

    '& > div': {
      maxWidth: '100%',
    },
  },

  '@media (max-width: 480px)': {
    padding: '30px 20px',
  },
});

export default VideoSection;
